import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Plus } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { useAppStore } from '../../store';
import { SampleTask } from '../../types';
import { supabase } from '../../lib/supabase';
import EntityModal from './EntityModal';
import ErrorModal from './ErrorModal';
import { formatCurrency } from '../../utils/formatters';
import { adjustDateForInput, toDBFriendlyDate } from '../../utils/dateUtils';

interface SampleTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (task: Omit<SampleTask, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  sampleId: string;
  initialData?: SampleTask;
}

const SampleTaskModal: React.FC<SampleTaskModalProps> = ({
  isOpen,
  onClose,
  onSave,
  sampleId,
  initialData,
}) => {
  const { suppliers, addSupplier, setError, addInvoice } = useAppStore();
  const [formData, setFormData] = useState({
    task_name: '',
    supplier_id: null as string | null,
    cost: '0',
    status: 'pending' as const,
    start_date: null as string | null,
    end_date: null as string | null,
    notes: '',
  });
  const [taskTypes, setTaskTypes] = useState<{id: string, name: string}[]>([]);
  const [selectedTaskType, setSelectedTaskType] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTaskTypeModalOpen, setIsTaskTypeModalOpen] = useState(false);
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const [newTaskTypeName, setNewTaskTypeName] = useState('');
  const [formErrors, setFormErrors] = useState({
    task_name: false,
    supplier_id: false,
    task_type_id: false,
    cost: false
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [supplierSearchQuery, setSupplierSearchQuery] = useState('');

  useEffect(() => {
    // Fetch task types
    const fetchTaskTypes = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('task_types')
          .select('id, name')
          .order('name');
        
        if (error) throw error;
        setTaskTypes(data || []);
      } catch (error) {
        console.error('Error fetching task types:', error);
        setErrorMessage('Error al cargar los tipos de tarea');
        setShowErrorModal(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchTaskTypes();
    }
  }, [isOpen]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        task_name: initialData.task_name || '',
        supplier_id: initialData.supplier_id,
        cost: initialData.cost.toString() || '0',
        status: initialData.status || 'pending',
        start_date: initialData.start_date ? adjustDateForInput(initialData.start_date) : null,
        end_date: initialData.end_date ? adjustDateForInput(initialData.end_date) : null,
        notes: initialData.notes || '',
      });
      setSelectedTaskType(initialData.task_type_id || null);
      setFormErrors({
        task_name: false,
        supplier_id: false,
        task_type_id: false,
        cost: false
      });
    } else {
      // Reset form for new task
      setFormData({
        task_name: '',
        supplier_id: null,
        cost: '0',
        status: 'pending',
        start_date: null,
        end_date: null,
        notes: '',
      });
      setSelectedTaskType(null);
      setFormErrors({
        task_name: false,
        supplier_id: false,
        task_type_id: false,
        cost: false
      });
    }
  }, [initialData, isOpen]);

  const validateForm = () => {
    const errors = {
      task_name: !formData.task_name.trim(),
      supplier_id: !formData.supplier_id,
      task_type_id: !selectedTaskType,
      cost: !formData.cost || parseFloat(formData.cost) <= 0
    };
    
    setFormErrors(errors);
    
    return !Object.values(errors).some(error => error);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!validateForm()) {
      return;
    }
    
    // Prepare dates for database storage
    const processedStartDate = formData.start_date ? toDBFriendlyDate(formData.start_date) : null;
    const processedEndDate = formData.end_date ? toDBFriendlyDate(formData.end_date) : null;
    
    const taskData = {
      sample_id: sampleId,
      task_name: formData.task_name.trim(),
      supplier_id: formData.supplier_id,
      task_type_id: selectedTaskType,
      cost: parseFloat(formData.cost || '0'),
      status: formData.status,
      start_date: processedStartDate,
      end_date: processedEndDate,
      notes: formData.notes || '',
    };
    
try {
  // Save the task - NO invoice creation
  await onSave(taskData);
  onClose();
} catch (error) {
  console.error('Error saving task:', error);
  setErrorMessage('Error al guardar la tarea');
  setShowErrorModal(true);
}
  };

  const handleCreateTaskType = async () => {
    if (!newTaskTypeName.trim()) {
      setErrorMessage('El nombre del tipo de tarea es obligatorio');
      setShowErrorModal(true);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('task_types')
        .insert([{ name: newTaskTypeName.trim() }])
        .select()
        .single();

      if (error) throw error;

      // Add to local state
      setTaskTypes(prev => [...prev, data]);
      setSelectedTaskType(data.id);
      setFormErrors(prev => ({ ...prev, task_type_id: false }));
      
      // Reset form and close modal
      setNewTaskTypeName('');
      setIsTaskTypeModalOpen(false);
    } catch (error) {
      console.error('Error creating task type:', error);
      setErrorMessage('Error al crear el tipo de tarea');
      setShowErrorModal(true);
    }
  };

  // Format cost as currency
  const formatCostAsCurrency = (value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '';
    return formatCurrency(numValue);
  };

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={onClose}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-40" />
          <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[800px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white dark:bg-gray-800 p-6 shadow-lg overflow-y-auto z-40" aria-describedby={undefined}>
            <Dialog.Title className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
              {initialData ? 'Editar' : 'Nueva'} Tarea de Muestra
            </Dialog.Title>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <Input
                  label="Nombre de la tarea"
                  id="task_name"
                  name="task_name"
                  value={formData.task_name}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, task_name: e.target.value }));
                    if (e.target.value.trim()) {
                      setFormErrors(prev => ({ ...prev, task_name: false }));
                    }
                  }}
                  required
                  error={formErrors.task_name ? "El nombre de la tarea es obligatorio" : undefined}
                />

<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
  {isLoading ? (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Tipo de tarea
      </label>
      <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
        Cargando tipos de tarea...
      </div>
    </div>
  ) : (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Tipo de tarea
        <span className="text-red-500 ml-1">*</span>
      </label>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Select
            label=""
            value={selectedTaskType || ''}
            onChange={(value) => {
              setSelectedTaskType(value ? value : null);
              setFormErrors(prev => ({ ...prev, task_type_id: !value }));
            }}
            options={[
              { value: '', label: 'Seleccionar tipo de tarea' },
              ...taskTypes.map(type => ({
                value: type.id,
                label: type.name
              }))
            ]}
            required
          />
          {formErrors.task_type_id && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">El tipo de tarea es obligatorio</p>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsTaskTypeModalOpen(true)}
          icon={<Plus className="h-4 w-4" />}
          className="h-12 flex items-center justify-center"
        >
          Nuevo
        </Button>
      </div>
    </div>
  )}

  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      Proveedor
      <span className="text-red-500 ml-1">*</span>
    </label>
    <div className="flex items-center gap-2">
      <div className="flex-1">
        <Select
          label=""
          value={formData.supplier_id || ''}
          onChange={(value) => {
            setFormData(prev => ({ ...prev, supplier_id: value ? value : null }));
            setFormErrors(prev => ({ ...prev, supplier_id: !value }));
          }}
          searchable={true}
          searchQuery={supplierSearchQuery}
          onSearchChange={setSupplierSearchQuery}
          options={[
            { value: '', label: 'Seleccionar proveedor' },
            ...suppliers.map(supplier => ({
              value: supplier.id,
              label: supplier.name,
            }))
          ]}
          required
        />
        {formErrors.supplier_id && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">El proveedor es obligatorio</p>
        )}
      </div>
      <Button
        type="button"
        variant="outline"
        onClick={() => setIsSupplierModalOpen(true)}
        icon={<Plus className="h-4 w-4" />}
        className="h-12 flex items-center justify-center"
      >
        Nuevo
      </Button>
    </div>
  </div>
</div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      Costo
      <span className="text-red-500 ml-1">*</span>
    </label>
    <div className="relative">
      <input
        type="number"
        id="cost"
        name="cost"
        value={formData.cost}
        onChange={(e) => {
          setFormData(prev => ({ ...prev, cost: e.target.value }));
          setFormErrors(prev => ({ ...prev, cost: !e.target.value || parseFloat(e.target.value) <= 0 }));
        }}
        min="0"
        step="0.01"
        required
        className="w-full h-12 pl-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
      />
      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500 dark:text-gray-400">
        {formData.cost && !isNaN(parseFloat(formData.cost)) ? formatCostAsCurrency(formData.cost).replace(/\d/g, '') : '$'}
      </div>
    </div>
    {formErrors.cost && (
      <p className="mt-1 text-sm text-red-600 dark:text-red-400">El costo debe ser mayor a 0</p>
    )}
    {!formErrors.cost && formData.cost && !isNaN(parseFloat(formData.cost)) && (
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {formatCostAsCurrency(formData.cost)}
      </p>
    )}
  </div>

  <Select
    label="Estado"
    value={formData.status}
    onChange={(value) => setFormData(prev => ({ ...prev, status: value as SampleTask['status'] }))}
    options={[
      { value: 'pending', label: 'Pendiente' },
      { value: 'in_progress', label: 'En Proceso' },
      { value: 'finished', label: 'Terminado' },
    ]}
    required
  />
</div>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
  <Input
    type="date"
    label="Fecha de inicio"
    id="start_date"
    name="start_date"
    value={formData.start_date || ''}
    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value || null }))}
  />

  <Input
    type="date"
    label="Fecha de fin"
    id="end_date"
    name="end_date"
    value={formData.end_date || ''}
    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value || null }))}
  />
</div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Observaciones
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                    placeholder="Agregar observaciones adicionales..."
                  />
                </div>

              
              </div>
              
              <div className="mt-6 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                >
                  {initialData ? 'Guardar Cambios' : 'Crear Tarea'}
                </Button>
              </div>
            </form>
            
            <Dialog.Close asChild>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                aria-label="Close"
              >
                <X className="h-6 w-6" />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Task Type Modal */}
      <Dialog.Root open={isTaskTypeModalOpen} onOpenChange={setIsTaskTypeModalOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[800px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white dark:bg-gray-800 p-6 shadow-lg z-50" aria-describedby={undefined}>
            <Dialog.Title className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
              Nuevo Tipo de Tarea
            </Dialog.Title>
            
            <div className="space-y-4">
              <Input
                label="Nombre"
                id="taskTypeName"
                name="taskTypeName"
                value={newTaskTypeName}
                onChange={(e) => setNewTaskTypeName(e.target.value)}
                required
              />
              
              <div className="mt-6 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsTaskTypeModalOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={handleCreateTaskType}
                  disabled={!newTaskTypeName.trim()}
                >
                  Crear Tipo de Tarea
                </Button>
              </div>
            </div>
            
            <Dialog.Close asChild>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                aria-label="Close"
              >
                <X className="h-6 w-6" />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Supplier Modal */}
      {isSupplierModalOpen && (
        <EntityModal
          isOpen={isSupplierModalOpen}
          onClose={() => setIsSupplierModalOpen(false)}
          entityType="supplier"
          onSave={async (supplier) => {
            try {
              await addSupplier(supplier);
              setIsSupplierModalOpen(false);
            } catch (error) {
              console.error('Error creating supplier:', error);
              setErrorMessage('Error al crear el proveedor');
              setShowErrorModal(true);
            }
          }}
        />
      )}

      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={errorMessage}
      />
    </>
  );
};

export default SampleTaskModal;